export interface CountryDigitRule {
  digits?: number;
  minDigits?: number;
  maxDigits?: number;
}

// Populated at runtime from the Firestore `country_codes` collection (see
// useCountryCodeSelection), keyed by dial code (e.g. "+91"). Each country defines
// either an exact `digits` count or a `minDigits`/`maxDigits` range — driven entirely
// by whatever's in Firestore, not hardcoded per-country regexes.
const countryDigitRules: Record<string, CountryDigitRule> = {};

export const setCountryDigitRules = (rules: Record<string, CountryDigitRule>) => {
  Object.assign(countryDigitRules, rules);
};

// Fallback range used only while Firestore data hasn't loaded yet, or for a dial
// code with no matching rule — a generic, permissive check (E.164 allows up to 15).
const FALLBACK_MIN = 4;
const FALLBACK_MAX = 15;

const digitRange = (countryCode: string): { min: number; max: number } => {
  const rule = countryDigitRules[countryCode];
  if (!rule) return { min: FALLBACK_MIN, max: FALLBACK_MAX };
  if (rule.digits != null) return { min: rule.digits, max: rule.digits };
  const min = rule.minDigits ?? rule.maxDigits ?? FALLBACK_MIN;
  const max = rule.maxDigits ?? rule.minDigits ?? FALLBACK_MAX;
  return { min, max };
};

export const validateMobile = (countryCode: string, mobile: string): boolean => {
  if (!/^\d+$/.test(mobile)) return false;
  const { min, max } = digitRange(countryCode);
  return mobile.length >= min && mobile.length <= max;
};

// Used as the input's maxLength — the upper bound of however many digits this
// country's rule allows.
export const getExpectedLength = (countryCode: string): number => {
  return digitRange(countryCode).max;
};

// For error messages — describes the rule accurately whether it's an exact count
// or a range (e.g. "exactly 10 digits" vs "10-11 digits"), instead of assuming
// every country wants exactly one length.
export const getDigitRuleDescription = (countryCode: string): string => {
  const { min, max } = digitRange(countryCode);
  return min === max ? `exactly ${min} digits` : `${min}-${max} digits`;
};

export const getFlagEmoji = (flag: string): string => {
  if (!flag) return '';
  if (/^[a-zA-Z]{2}$/.test(flag)) {
    try {
      const codePoints = flag
        .toUpperCase()
        .split('')
        .map(char => 127397 + char.charCodeAt(0));
      return String.fromCodePoint(...codePoints);
    } catch (e) {
      return flag;
    }
  }
  return flag;
};

export const getIsoFromEmoji = (flag: string): string => {
  if (!flag) return '';
  if (/^[a-zA-Z]{2}$/.test(flag)) {
    return flag.toLowerCase();
  }
  try {
    const chars = Array.from(flag);
    const letters = chars.map(char => {
      const code = char.codePointAt(0);
      if (code && code >= 127462 && code <= 127487) {
        return String.fromCharCode(code - 127397);
      }
      return '';
    }).join('');
    if (letters.length === 2) {
      return letters.toLowerCase();
    }
  } catch (e) {
    // fallback
  }
  return 'in';
};
